import { Line } from "@nivo/line";
import { useMemo } from "react";

import { EMPTY_COLOR, isEmptyGroup, LINE_COLOR_ORDER } from ".";

import { ChartData } from "@/__generated__/graphql";
import {
  fittingAxisLabels,
  useFittedValueAxis,
  formatLabel,
  AXIS_NAME_ROOM,
  formatValue,
  toLineData,
  useAnimatesOnChange,
  usePlotSize,
} from "@/components/organisms/Chart/Chart.utils";
import { ChartTooltip } from "@/components/organisms/Chart/ChartTooltip";

import { typographyStyles } from "@/components/ui/typography";

const SIZE_MARGINS = {
  xs: { top: 8, right: 0, bottom: 40, left: 48 },
  sm: { top: 12, right: 0, bottom: 52, left: 60 },
  md: { top: 16, right: 0, bottom: 64, left: 72 },
  lg: { top: 20, right: 0, bottom: 76, left: 84 },
};

const SIZE_CONFIGS = {
  xs: {
    lineWidth: 1.5,
    pointSize: 6,
    tickValues: 3,
    enableGridX: false,
    enableGridY: true,
  },
  sm: {
    lineWidth: 2,
    pointSize: 8,
    tickValues: 4,
    enableGridX: false,
    enableGridY: true,
  },
  md: {
    lineWidth: 2.5,
    pointSize: 10,
    tickValues: 5,
    enableGridX: true,
    enableGridY: true,
  },
  lg: {
    lineWidth: 3,
    pointSize: 12,
    tickValues: 6,
    enableGridX: true,
    enableGridY: true,
  },
};

export function LineChart({
  data,
  size = "md",
}: {
  data: ChartData;
  size?: "xs" | "sm" | "md" | "lg";
}) {
  const lineData = useMemo(() => toLineData(data), [data]);
  // A line has no area to hatch, so the line for rows the grouping says
  // nothing about is drawn flat in the same grey the hatch is struck in.
  const seriesColors = useMemo(
    () =>
      lineData.map((series, index) =>
        isEmptyGroup(series.id)
          ? EMPTY_COLOR
          : LINE_COLOR_ORDER[index % LINE_COLOR_ORDER.length],
      ),
    [lineData],
  );
  const sizeConfig = SIZE_CONFIGS[size];

  const {
    ref: wrapperRef,
    width: plotWidth,
    height: plotHeight,
  } = usePlotSize();

  // Wide enough for the numbers the value axis turned out to carry.
  const valueGutter = useFittedValueAxis(wrapperRef, lineData);
  const margins = useMemo(() => {
    const base = SIZE_MARGINS[size];
    // Room for the words beside each axis, on top of the room the numbers
    // themselves need.
    return {
      ...base,
      left:
        Math.max(base.left, valueGutter) +
        (data.valueAxis ? AXIS_NAME_ROOM : 0),
      bottom: base.bottom + (data.labelAxis ? AXIS_NAME_ROOM : 0),
    };
  }, [size, valueGutter, data.labelAxis, data.valueAxis]);

  const animates = useAnimatesOnChange(data);

  const bottomTickValues = useMemo(
    () =>
      fittingAxisLabels(
        data.points.map((point) => point.label),
        data.labelFormat,
        // Rotated -30°, so a label only occupies its width along the axis.
        Math.max(plotWidth - margins.left - margins.right, 0) / 0.87,
      ),
    [data.labelFormat, data.points, margins.left, margins.right, plotWidth],
  );

  // A period with no movement: every point at zero collapses nivo's auto
  // domain and floats the baseline through the middle of an empty plot.
  const isFlat = useMemo(
    () => lineData.every((series) => series.data.every((d) => d.y === 0)),
    [lineData],
  );

  // A line's path is animated from the one before it, so a chart that changes
  // what it is plotted against - a different period, one more month - drags
  // every point sideways into its new place. Keyed by what sits along the
  // axis, a change of that kind draws afresh instead of sliding; a change in
  // the numbers alone keeps the same key, and animates, which is only ever
  // upwards or downwards.
  const alongTheAxis = useMemo(
    () =>
      lineData
        .map((series) => series.data.map((point) => point.x).join("|"))
        .join("~"),
    [lineData],
  );

  return (
    <div
      ref={wrapperRef}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
    >
      {plotWidth > 0 && plotHeight > 0 && (
        <Line
          key={alongTheAxis}
          width={plotWidth}
          height={plotHeight}
          animate={animates}
          isInteractive={true}
          data={lineData}
          margin={margins}
          yScale={{ type: "linear", min: 0, max: isFlat ? 1 : "auto" }}
          theme={{
            grid: {
              line: {
                stroke: "var(--border)",
              },
            },
            axis: {
              domain: {
                line: {
                  stroke: "var(--border)",
                },
              },
              legend: {
                text: {
                  // The axis's name is set in the app's own type, not in the
                  // chart library's default sans: a chart with two typefaces in
                  // it reads as two charts.
                  fontFamily: typographyStyles["body-sm"].fontFamily as string,
                  fontSize: typographyStyles["body-sm"].fontSize as string,
                  fontWeight: 600,
                  fill: "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
                },
              },
            },
          }}
          lineWidth={sizeConfig.lineWidth}
          pointSize={sizeConfig.pointSize}
          enableGridX={sizeConfig.enableGridX}
          enableGridY={sizeConfig.enableGridY}
          colors={seriesColors}
          // Hovered anywhere along the plot, not only on a point: with neither
          // a mesh nor slices, nivo answers the pointer with nothing at all and
          // the chart had no tooltip whatsoever. A slice is the whole column at
          // one moment, which is the question being asked anyway - what was
          // each line worth here.
          enableSlices="x"
          sliceTooltip={({ slice }) => (
            <ChartTooltip
              values={[...slice.points]}
              datum={slice as any}
              size={size}
              colors={seriesColors}
              checkIsActiveValue={() => false}
              keyColumnHeader={formatLabel(
                String(slice.points[0]?.data?.x ?? ""),
                data.labelFormat,
              )}
              valueColumnHeader="Value"
              renderKeyColumn={(_hovered, point: any) => point.seriesId}
              // Each row is a line of its own, read off that line where the
              // pointer is.
              renderValueColumn={(_hovered, point: any) =>
                formatValue(point.data.y ?? 0, data.valueFormat)
              }
            />
          )}
          axisBottom={{
            // What the axis is of, said once beside it rather than left for a
            // reader to work out from the ticks.
            legend: data.labelAxis || undefined,
            legendPosition: "middle",
            // As far out as the room made for it, so it clears the ticks
            // however far they lean.
            legendOffset: margins.bottom - 6,
            tickValues: bottomTickValues,
            renderTick: (props) => {
              const { x, y, value } = props;
              const formatted = formatLabel(String(value), data.labelFormat);

              return (
                <g transform={`translate(${x},${y})`}>
                  <rect
                    width="1"
                    height="5"
                    fill={"var(--foreground)"}
                    transform="translate(-0.5,0.5)"
                  />
                  <g transform="rotate(-30)">
                    <text
                      x={-4}
                      y={0}
                      dy={20}
                      textAnchor="end"
                      fontFamily={
                        typographyStyles["body-md"].fontFamily as string
                      }
                      fontSize={typographyStyles["body-md"].fontSize as string}
                      fill={
                        "color-mix(in oklab, var(--muted-foreground) 80%, transparent)"
                      }
                    >
                      {formatted}
                    </text>
                  </g>
                </g>
              );
            },
          }}
          axisLeft={{
            legend: data.valueAxis || undefined,
            legendPosition: "middle",
            legendOffset: -(margins.left - 10),
            tickValues: isFlat ? [0] : sizeConfig.tickValues,
            renderTick: (props) => {
              const { x, y, value } = props;
              const formatted = formatValue(value, data.valueFormat);

              return (
                <g transform={`translate(${x},${y})`}>
                  <rect
                    width="5"
                    height="1"
                    fill={"var(--foreground)"}
                    transform="translate(-5,-0.5)"
                  />
                  <text
                    data-axis="value"
                    x={-8}
                    y={0}
                    dy={4}
                    textAnchor="end"
                    fontFamily={
                      typographyStyles["body-md"].fontFamily as string
                    }
                    fontSize={typographyStyles["body-md"].fontSize as string}
                    fill={
                      "color-mix(in oklab, var(--muted-foreground) 80%, transparent)"
                    }
                  >
                    {formatted}
                  </text>
                </g>
              );
            },
          }}
        />
      )}
    </div>
  );
}
